/**
 * Mock data + helpers. These are used when no backend is available so that
 * the UI is fully functional in standalone mode.
 */

export const PLATFORMS = [
  { id: 'facebook', label: 'Facebook', color: '#1877F2' },
  { id: 'instagram', label: 'Instagram', color: '#E1306C' },
  { id: 'linkedin', label: 'LinkedIn', color: '#0A66C2' },
  { id: 'tiktok', label: 'TikTok', color: '#010101' },
  { id: 'x', label: 'X', color: '#000000' },
];

export function uid(prefix = 'id') {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

export function daysFromNow(days, hour = 10, minute = 0) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
}

export const mockInitialPosts = [
  {
    id: uid('post'),
    caption:
      'Kicking off our spring product launch! 🌱 New features rolling out this week across every platform.',
    mediaUrls: [],
    platforms: ['facebook', 'linkedin'],
    scheduledAt: daysFromNow(1, 9, 30),
    status: 'scheduled',
    hashtags: ['#productlaunch', '#spring', '#new'],
    createdAt: daysFromNow(-2),
    // Mock campaign embed — matches the shape PostResource returns for real.
    campaign_id: 1,
    campaign: { id: 1, name: 'Spring Launch', color: '#3b64ff', status: 'active' },
  },
  {
    id: uid('post'),
    caption: 'Behind-the-scenes peek at our team workshop ✨',
    mediaUrls: [],
    platforms: ['instagram', 'tiktok'],
    scheduledAt: daysFromNow(3, 17, 0),
    status: 'scheduled',
    hashtags: ['#bts', '#team'],
    createdAt: daysFromNow(-1),
    campaign_id: null,
    campaign: null,
  },
  {
    id: uid('post'),
    caption: 'Top 3 productivity tips we learned this quarter 🧠',
    mediaUrls: [],
    platforms: ['linkedin'],
    scheduledAt: daysFromNow(-5, 14, 0),
    status: 'published',
    hashtags: ['#productivity'],
    createdAt: daysFromNow(-10),
    campaign_id: 2,
    campaign: { id: 2, name: 'Weekly Tips', color: '#10b981', status: 'active' },
  },
  {
    id: uid('post'),
    caption: 'Draft: announcing the winners of our giveaway 🏆',
    mediaUrls: [],
    platforms: ['instagram', 'facebook'],
    scheduledAt: null,
    status: 'draft',
    hashtags: [],
    createdAt: daysFromNow(-3),
    campaign_id: null,
    campaign: null,
  },
];

export const mockSocialAccounts = [
  {
    id: 'facebook',
    platform: 'facebook',
    handle: null,
    connected: false,
  },
  {
    id: 'instagram',
    platform: 'instagram',
    handle: '@your_studio',
    connected: true,
  },
  {
    id: 'linkedin',
    platform: 'linkedin',
    handle: 'Your Company',
    connected: true,
  },
  {
    id: 'tiktok',
    platform: 'tiktok',
    handle: null,
    connected: false,
  },
];

export const mockActivity = [
  {
    id: uid('a'),
    type: 'published',
    text: 'Post "Top 3 productivity tips..." published to LinkedIn',
    at: daysFromNow(-5, 14, 2),
  },
  {
    id: uid('a'),
    type: 'scheduled',
    text: 'New post scheduled for Facebook + LinkedIn',
    at: daysFromNow(-2, 12, 15),
  },
  {
    id: uid('a'),
    type: 'connected',
    text: 'LinkedIn account connected',
    at: daysFromNow(-7, 9, 0),
  },
  {
    id: uid('a'),
    type: 'draft',
    text: 'Draft saved: "announcing the winners..."',
    at: daysFromNow(-3, 11, 40),
  },
];

// AI mock helpers -----------------------------------------------------------
const STOPWORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'but', 'if', 'then', 'so', 'of', 'for',
  'to', 'in', 'on', 'at', 'by', 'from', 'with', 'as', 'is', 'are', 'was',
  'were', 'be', 'been', 'being', 'this', 'that', 'it', 'its', 'we', 'our',
  'you', 'your', 'my', 'i', 'me', 'they', 'them', 'their', 'just', 'about',
]);

function extractKeywords(caption = '', limit = 8) {
  const words = caption
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((w) => w.length >= 3 && !STOPWORDS.has(w));
  const counts = new Map();
  for (const w of words) counts.set(w, (counts.get(w) || 0) + 1);
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([w]) => w);
}

const PLATFORM_TAGS = {
  instagram: ['#instagood', '#photooftheday', '#reels', '#love', '#explore'],
  facebook: ['#community', '#update', '#news'],
  linkedin: ['#leadership', '#business', '#growth', '#innovation'],
  tiktok: ['#fyp', '#foryou', '#trending', '#viral'],
  x: ['#trending', '#breaking', '#news', '#tech'],
};

export function mockGenerateHashtags(caption, platform) {
  const kw = extractKeywords(caption);
  const base = kw.map((w) => `#${w.replace(/\s+/g, '')}`);
  const platformTags = PLATFORM_TAGS[platform] || PLATFORM_TAGS.instagram;
  const combined = [...base, ...platformTags];
  // Dedup and trim
  const seen = new Set();
  const unique = [];
  for (const t of combined) {
    const key = t.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(t);
    }
  }
  // If caption is empty, return platform defaults + generic
  if (unique.length === 0) {
    return ['#socialmedia', '#content', '#marketing', ...platformTags];
  }
  return unique.slice(0, 12);
}

// Platform-tuned rough best-posting-hour heuristics (local time).
const BEST_HOUR_MAP = {
  instagram: [11, 14, 19],
  facebook: [9, 13, 15],
  linkedin: [8, 12, 17],
  tiktok: [18, 20, 22],
  x: [7, 12, 17, 21],
};

function makeTimeOnDay(offsetDays, hour, minute = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
}

export function mockBestTime(platform) {
  const hours = BEST_HOUR_MAP[platform] || BEST_HOUR_MAP.instagram;
  const tomorrowHours = hours.map((h) => makeTimeOnDay(1, h));
  const dayAfter = hours.map((h) => makeTimeOnDay(2, h, 30));
  const all = [...tomorrowHours, ...dayAfter];
  return {
    best_time: all[0],
    alternatives: all.slice(1, 5),
  };
}
